import { useId, type ReactNode } from 'react'

// The places' own icons, drawn for gatheredfrom on a 24px grid with one rounded 1.75 stroke, the way Instagram's
// tabs are: an outline normally, filled in for the place you're on. Details inside a filled icon are cut out with
// a mask rather than painted over, so the frosted tab bar shows through them.
export type PlaceIconProps = { className?: string; active?: boolean }

function Icon({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      {children}
    </svg>
  )
}

// A recipe box: a tin with a lined card leaning up out of it, the card that's kept. Filled, the tin goes solid
// with its label slot cut out and the card stays drawn.
export function RecipesIcon({ className, active }: PlaceIconProps) {
  const mask = useId()
  const card = 'M7.4 12.5 6.6 7.3a1.4 1.4 0 0 1 1.17-1.6l7.9-1.2a1.4 1.4 0 0 1 1.6 1.17l1 6.83'
  const line = 'M9.2 8.6l5.4-.8'
  const tin = 'M3.5 12.5h17v5a2.5 2.5 0 0 1-2.5 2.5H6a2.5 2.5 0 0 1-2.5-2.5Z'
  const label = 'M10 16.25h4'
  if (!active)
    return (
      <Icon className={className}>
        <path d={card} />
        <path d={line} />
        <path d={tin} />
        <path d={label} />
      </Icon>
    )
  return (
    <Icon className={className}>
      <mask id={mask}>
        <rect width="24" height="24" fill="white" />
        <path d={label} stroke="black" strokeWidth={1.75} />
      </mask>
      <path d={card} />
      <path d={line} />
      <path d={tin} fill="currentColor" mask={`url(#${mask})`} />
    </Icon>
  )
}

export function PeopleIcon({ className, active }: PlaceIconProps) {
  const fill = active ? 'currentColor' : 'none'
  return (
    <Icon className={className}>
      <circle cx="9.25" cy="8" r="3.25" fill={fill} />
      <path d="M3.5 19.25c0-3.2 2.6-5.5 5.75-5.5S15 16.05 15 19.25Z" fill={fill} />
      <path d="M15.25 4.95a3.25 3.25 0 0 1 0 6.1" />
      <path d="M17 14.1c2.1.6 3.5 2.6 3.5 5.15" />
    </Icon>
  )
}

export function PhotosIcon({ className, active }: PlaceIconProps) {
  const mask = useId()
  const frame = <rect x="3.5" y="4.5" width="17" height="15" rx="4" />
  const hills = 'M4 16.5l4.4-4.4a1.6 1.6 0 0 1 2.25 0l5.6 5.6M14.25 15.5l1.4-1.4a1.6 1.6 0 0 1 2.25 0l2.35 2.35'
  if (!active)
    return (
      <Icon className={className}>
        {frame}
        <path d={hills} />
        <circle cx="15.5" cy="9.25" r="1.5" />
      </Icon>
    )
  return (
    <Icon className={className}>
      <mask id={mask}>
        <rect width="24" height="24" fill="white" />
        <path d={hills} stroke="black" strokeWidth={1.5} />
        <circle cx="15.5" cy="9.25" r="1.6" fill="black" stroke="none" />
      </mask>
      <rect x="3.5" y="4.5" width="17" height="15" rx="4" fill="currentColor" mask={`url(#${mask})`} />
    </Icon>
  )
}

// A campfire, for the stories told around one: a flame with a curling side tongue (so it can't be mistaken for a
// raindrop) over two crossed logs. Filled, the flame goes solid and the logs stay drawn.
export function MomentsIcon({ className, active }: PlaceIconProps) {
  const flame =
    'M9.48 12.04A1.8 1.8 0 0 0 11.28 10.24c0-.99-.36-1.44-.72-2.16-.77-1.54-.16-2.92 1.44-4.32.36 1.8 1.44 3.53 2.88 4.68 1.44 1.15 2.16 2.52 2.16 3.96a5.04 5.04 0 1 1-10.08 0c0-.83.31-1.65.72-2.16a1.8 1.8 0 0 0 1.8 1.8Z'
  return (
    <Icon className={className}>
      <path d={flame} fill={active ? 'currentColor' : 'none'} />
      <path d="M4.75 20.5l14.5-3.25M19.25 20.5 4.75 17.25" />
    </Icon>
  )
}

// A house with a rounded roof and a door: the circle's home page.
const house = 'M4.5 10.6 11 5.05a1.55 1.55 0 0 1 2 0l6.5 5.55V18.4a1.6 1.6 0 0 1-1.6 1.6H6.1a1.6 1.6 0 0 1-1.6-1.6Z'

export function HomeIcon({ className, active }: PlaceIconProps) {
  const mask = useId()
  const door = 'M10 20v-4.25a2 2 0 0 1 4 0V20'
  if (!active)
    return (
      <Icon className={className}>
        <path d={house} />
        <path d={door} />
      </Icon>
    )
  return (
    <Icon className={className}>
      <mask id={mask}>
        <rect width="24" height="24" fill="white" />
        <path d="M10.6 21v-5.1a1.4 1.4 0 0 1 2.8 0V21Z" fill="black" stroke="none" />
      </mask>
      <path d={house} fill="currentColor" mask={`url(#${mask})`} />
    </Icon>
  )
}

// A wall calendar: a page with its two rings and a day marked. Filled, the page goes solid with the marked day and
// the line under the rings cut out.
export function CalendarIcon({ className, active }: PlaceIconProps) {
  const mask = useId()
  const page = 'M6 5h12a2.5 2.5 0 0 1 2.5 2.5v10.5a2.5 2.5 0 0 1-2.5 2.5H6a2.5 2.5 0 0 1-2.5-2.5V7.5A2.5 2.5 0 0 1 6 5Z'
  const rings = 'M8 3v4M16 3v4'
  if (!active)
    return (
      <Icon className={className}>
        <path d={page} />
        <path d={rings} />
        <path d="M3.5 10h17" />
        <rect x="13.5" y="13.5" width="3" height="3" rx="0.75" />
      </Icon>
    )
  return (
    <Icon className={className}>
      <mask id={mask}>
        <rect width="24" height="24" fill="white" />
        <path d="M3.5 10h17" stroke="black" />
        <rect x="13.5" y="13.5" width="3" height="3" rx="0.75" fill="black" stroke="none" />
      </mask>
      <path d={page} fill="currentColor" mask={`url(#${mask})`} />
      <path d={rings} />
    </Icon>
  )
}

// The rest of the places, when there are more than the phone's tabs hold: four squares, filled in when you're on one
// of them.
export function MoreIcon({ className, active }: PlaceIconProps) {
  const fill = active ? 'currentColor' : undefined
  return (
    <Icon className={className}>
      {[
        [4, 4],
        [13.5, 4],
        [4, 13.5],
        [13.5, 13.5],
      ].map(([x, y]) => (
        <rect key={`${x}-${y}`} x={x} y={y} width="6.5" height="6.5" rx="1.75" fill={fill} />
      ))}
    </Icon>
  )
}
